/**
 * A metric, optionally with its history.
 *
 * ONE QUERY ANSWERS ALL THREE NUMBERS. When the spec groups by a time
 * dimension, the result is that measure per period: the last is the value, the
 * one before it is what the movement is measured against, and the whole series
 * is the sparkline. Asking separately for "this month" and "last month" would
 * be two questions that can disagree — different filters, different scope
 * resolution, a boundary crossed between them — and a delta is exactly the
 * place where two nearly-right answers produce a confidently wrong one.
 *
 * The number and the delta are drawn here, in text. The sparkline is NOT: it
 * comes in as an already-rendered element from whatever chart adapter the host
 * provided, so a metric tile cannot end up with a second, hand-rolled chart
 * implementation living inside it.
 */
import type {
  MetricSelection,
  QueryResult,
  ReportModel,
  ReportSpec,
} from '@clockwise/semantic-core';
import {
  findMeasure,
  MONTH_NAMES,
  type ResolvedFormat,
  resolveValueFormat,
} from '@clockwise/semantic-core';
import type { CSSProperties, ReactNode } from 'react';
import { cellClass, formatResolved } from '../format';

/**
 * A metric's figure over the reader's whole window, and over its mirror.
 *
 * Asked by the host as two queries over the two windows, because a window
 * longer than one grain cannot be added up from the series — a quarter's
 * utilization is not the sum, nor the average, of three monthly ones.
 */
export interface MetricComparison {
  /** The measure over the reader's window (to today, while it runs). */
  value: number | null;
  /** The measure over the mirror window. `null` = asked, and no number. */
  previous: number | null;
  /** The reader's window, named — `Q3 2026`. */
  period: string;
  /** The mirror, named — `Q2` — or its dates, for a range with no name. */
  against: string;
  /**
   * The dates the mirror covers — `Apr 1 – Jun 29, 2026` — shown on hover.
   * The label names the period; this says it was cut to the same day.
   */
  againstRange?: string;
  /**
   * The reader's window is still running. Said beside the figure — and still
   * COMPARED, against whatever mirror the host chose: the previous quarter to
   * the same day for a quarter, the whole of last month for a month.
   */
  toDate: boolean;
}

export interface MetricValueProps {
  model: ReportModel;
  spec: ReportSpec;
  result: QueryResult;
  /**
   * The measure per period, oldest first, when the spec trends over time.
   *
   * Empty or absent means the spec has no time axis, and the value is read
   * from the single row instead.
   */
  series?: readonly (number | null)[];
  /**
   * The period LABELS behind `series`, for naming what is being reported.
   *
   * Labels, not keys: a key is an identity the rules compare dates with, and
   * for several dimensions it is not readable — Budget Period is keyed
   * `2026-08-01` and labelled `Aug 2026`.
   */
  periods?: readonly string[];
  /**
   * Which period to report, and what to compare it against.
   *
   * Decided by `selectMetricPeriods`, which needs the window the READER chose
   * — something this component cannot know, because the spec's own window has
   * been widened to give the sparkline its history.
   */
  selection?: MetricSelection | null;
  /** The sparkline, already drawn by the host's chart adapter. */
  sparkline?: ReactNode;
  /**
   * What the surrounding chrome already calls this number.
   *
   * A dashboard tile's header names the tile, and the block below names the
   * measure — which on the ordinary metric tile, whose title IS the measure's
   * label, is the same words twice under the same number. Given a heading that
   * matches, the label drops out and the header keeps the naming. Given one
   * that differs ("Hours this year" over `Total Logged Hours`) it stays: there
   * the label is the only thing saying which measure the number is.
   */
  heading?: string;
  /**
   * The figure was clicked: drill into it. Absent and the number is plain text.
   * Handed the element it was drawn in, for the menu to open beside.
   */
  onDrill?: (el: HTMLElement, text: string) => void;
  /**
   * The host names the measure somewhere else — a dashboard tile's ⓘ opens its
   * definition under its label — so the line under the number is dropped even
   * on a retitled tile. The number, its period and its movement are the tile.
   */
  measureNamed?: boolean;
  /**
   * The host already shows the window being reported — a dashboard's date
   * chip — so a whole-window figure (`comparison`) drops its period line.
   * "Q3 2026 · to date" under every tile repeated the one chip above them all.
   * A figure picked off the series keeps it: that period is the series' last
   * complete grain, which is not what the chip says.
   */
  periodNamed?: boolean;
  /**
   * An explicit prior value, overriding the one the series implies.
   *
   * `undefined` means "not asked for"; `null` means asked for and there is no
   * number. The distinction is worth keeping: the second case still prints
   * "no prior period" rather than silently showing nothing.
   */
  previous?: number | null;
  /**
   * The figure and its comparison over whole windows. Given, it wins over
   * `series`/`selection` for the NUMBERS; the series is then only the
   * sparkline.
   */
  comparison?: MetricComparison;
}

export function MetricValue({
  model,
  spec,
  result,
  series,
  periods,
  selection,
  sparkline,
  previous,
  comparison,
  heading,
  measureNamed = false,
  periodNamed = false,
  onDrill,
}: MetricValueProps) {
  const measureId = spec.measures[0];
  if (!measureId) return null;
  const measure = findMeasure(model, measureId);
  const resolved = resolveValueFormat(measure, spec, measureId);

  // WHICH PERIOD, decided upstream. Reading "the last populated value" here
  // would report a one-day-old month against a whole one on the first of every
  // month — see `selectMetricPeriods`, which owns that rule and is tested on
  // the day it matters.
  const trended = selection != null && series !== undefined;

  const value = comparison
    ? comparison.value
    : trended
      ? (series[selection.current] ?? null)
      : (result.rows[0]?.values[measureId] ?? null);
  const prior = comparison
    ? comparison.previous
    : previous !== undefined
      ? previous
      : trended && selection.previous !== undefined
        ? (series[selection.previous] ?? null)
        : undefined;

  const period = comparison
    ? comparison.period
    : trended
      ? periods?.[selection.current]
      : undefined;
  const priorPeriod = comparison
    ? comparison.against
    : trended && selection.previous !== undefined
      ? periods?.[selection.previous]
      : undefined;
  const toDate = comparison ? comparison.toDate : trended && selection.toDate;
  // A one-grain pick is never compared while it runs; a whole-window
  // comparison always is, against the mirror the host chose for it.
  const compared = prior !== undefined && (comparison !== undefined || !toDate);

  // COMPARED, not assumed. The host is not required to have a header at all —
  // the builder preview has none — so the label is dropped only when something
  // above it is demonstrably already saying the same thing.
  const label = measure?.label ?? measureId;
  const namedAbove = measureNamed || (heading !== undefined && sameText(heading, label));
  const text = formatResolved(value, resolved);
  // The figure's set width, for the stylesheet to fit it to the tile's width:
  // a narrow tile shrinks "8.0%" rather than clipping it.
  const fit = { '--rk-metric-ems': figureEms(text) } as CSSProperties;

  return (
    <div className="rk-metric">
      <div className="rk-metric-figure">
        {onDrill && value !== null ? (
          // A real BUTTON, so Tab reaches it and Enter opens the menu — the
          // same reason a pivot cell's figure is one.
          <button
            type="button"
            className={['rk-metric-value rk-metric-drill', cellClass(value, resolved)]
              .filter(Boolean)
              .join(' ')}
            style={fit}
            aria-label={`${label}${period ? `, ${readablePeriod(period)}` : ''}: ${text}. Drill into this figure`}
            onClick={(e) => onDrill(e.currentTarget, text)}
          >
            {text}
          </button>
        ) : (
          <span
            // `cellClass`, not `negativeClass`: a highlight rule is offered for the
            // measure whatever the report is drawn as — `FORMAT_CONTROLS` keys off
            // the unit and knows nothing about `spec.viz` — so a tile that read
            // only the negative colour accepted a rule, showed nothing, and still
            // wrote the highlight into the workbook it exported to.
            className={['rk-metric-value', cellClass(value, resolved)].filter(Boolean).join(' ')}
            style={fit}
          >
            {text}
          </span>
        )}
        {/* NAME THE PERIOD. Without it nobody can tell whether the number is
            this month, last month or the year — and once the default is "the
            last COMPLETE period", the answer is not what a reader would assume
            from the date picker above it. Beside the figure rather than on a
            line of its own: it qualifies the number, and a line of its own made
            it the second-loudest thing in the tile. */}
        {period && !(periodNamed && comparison) && (
          <span className="rk-metric-period">
            {readablePeriod(period)}
            {toDate ? ' · to date' : ''}
          </span>
        )}
      </div>
      {!namedAbove && <span className="rk-metric-label">{label}</span>}
      {/* A running GRAIN picked off the series is shown uncompared: nothing
          chose what it should be measured against. A whole-window comparison
          always is — its mirror was chosen deliberately (see
          `comparisonWindows`). The history below tells the truth about shape. */}
      {compared && (
        <Delta
          value={value}
          previous={prior}
          resolved={resolved}
          against={priorPeriod ? shortPeriod(priorPeriod, period) : undefined}
          againstRange={comparison?.againstRange}
        />
      )}
      {sparkline && <div className="rk-metric-spark">{sparkline}</div>}
    </div>
  );
}

/** Two pieces of display text that a reader would read as the same name. */
const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Movement against the prior period, IN THE MEASURE'S OWN UNIT.
 *
 * Absolute, not relative. A percentage measure moves in POINTS: Holiday %
 * going from 0.5% to 1.2% is "0.7 pct", and the relative "140%" it used to
 * print was true arithmetic about a number nobody reasons in. The unit reads
 * "pct" (asked for 2026-09-30, over "pts"); it is still points, not a percent
 * of the prior value. Hours move in
 * hours. A relative change also needed a non-zero baseline and had to refuse
 * one; an absolute one does not.
 *
 * COLOURED BY DIRECTION: up is green, down is red. That is a reading of the
 * arrow, not a verdict on the business — Leave % rising is green too. It was
 * neutral until the leadership dashboard asked for the colour (2026-09-24);
 * `docs/dashboards.md` records the change.
 */
function Delta({
  value,
  previous,
  resolved,
  against,
  againstRange,
}: {
  value: number | null;
  previous: number | null;
  resolved: ResolvedFormat;
  /** The prior period's name, e.g. `Jul`. Absent says "prior period". */
  against?: string;
  /** The dates behind `against`, for the hover. */
  againstRange?: string;
}) {
  if (value === null || previous === null) {
    return <span className="rk-metric-delta rk-metric-delta-none">no prior period</span>;
  }
  const raw = value - previous;
  // DIRECTION FROM THE SHOWN NUMBER, not the raw one. Two separately summed
  // ratios differ by float noise, and a 1e-9 rise drawn as a green "0.0 pct"
  // is a coloured movement beside a zero. Rounded to the precision printed,
  // anything that reads as zero IS zero. (A currency scale divides after this,
  // so a K/M delta below one scaled unit can still show a direction — rare.)
  const step = 10 ** -(resolved.decimals + (resolved.format === 'percent' ? 2 : 0));
  // Rounded by MAGNITUDE, so a half-step rise and a half-step fall round the
  // same way — `Math.round` alone sends halves toward +∞.
  const change = Math.sign(raw) * Math.round(Math.abs(raw) / step) * step;
  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  const arrow = direction === 'up' ? '↗' : direction === 'down' ? '↘' : '→';
  // Rules, the zero style and accounting describe the VALUE. A movement is
  // unsigned here (the arrow carries the sign) and a zero one is still a
  // number — accounting and `zero: 'dash'` would print "-" for "no change".
  const amount =
    resolved.format === 'percent'
      ? `${(Math.abs(change) * 100).toFixed(resolved.decimals)} pct`
      : formatResolved(Math.abs(change), {
          ...resolved,
          rules: [],
          accounting: false,
          zero: 'number',
        });
  return (
    <span className={`rk-metric-delta rk-metric-delta-${direction}`}>
      {/* NAMED, not hidden: the amount is unsigned, so without this a screen
          reader hears a rise and a fall as the same sentence. */}
      <span role="img" aria-label={direction === 'flat' ? 'unchanged' : direction}>
        {arrow}
      </span>{' '}
      {amount}{' '}
      <span
        className="rk-metric-delta-against"
        title={againstRange ? `Compared with ${againstRange}` : undefined}
      >
        vs {against ?? 'prior period'}
        {/* Heard, not only hovered: `title` is skipped by most screen readers,
            and "vs Q2" alone reads as the whole quarter. */}
        {againstRange && <span className="rk-sr">, compared with {againstRange}</span>}
      </span>
    </span>
  );
}

const MONTH_KEY = /^(\d{4})-(\d{2})$/;

/**
 * A period label a reader recognises: `2026-08` reads `Aug 2026`.
 *
 * The Month dimension labels itself with its key, which sorts as text and is
 * right in a pivot header. Under a single number it reads as a code. Anything
 * that is not a bare month passes through untouched.
 */
export function readablePeriod(label: string): string {
  const m = MONTH_KEY.exec(label);
  const month = m ? MONTH_NAMES[Number(m[2]) - 1] : undefined;
  return m && month ? `${month} ${m[1]}` : label;
}

/** The prior period, short: `Jul`, or `Dec 2025` when the year changed. */
export function shortPeriod(label: string, current?: string): string {
  const m = MONTH_KEY.exec(label);
  const month = m ? MONTH_NAMES[Number(m[2]) - 1] : undefined;
  if (!m || !month) return label;
  const sameYear = current !== undefined && MONTH_KEY.exec(current)?.[1] === m[1];
  return sameYear ? month : `${month} ${m[1]}`;
}

/**
 * Glyph advances in the figure's face (the system UI font at weight 800, in
 * tabular figures), in em. Measured, not guessed: a flat per-character budget
 * under-counted '%' (0.92em against a digit's 0.67) and so still clipped
 * exactly the short percentages a narrow tile shows.
 */
const GLYPH_EMS: Readonly<Record<string, number>> = {
  '.': 0.25,
  ',': 0.25,
  ':': 0.25,
  ' ': 0.2,
  '-': 0.45,
  '−': 0.6,
  '%': 0.92,
  $: 0.66,
  '—': 0.87,
  M: 0.87,
};
const DIGIT_EMS = 0.67;
/** A glyph not measured above: a capital's width, which is on the safe side. */
const OTHER_EMS = 0.8;
/** The figure's own `letter-spacing`, which every glyph after the first gives back. */
const TRACKING_EMS = -0.02;
/** Headroom for other platforms' system fonts, which set a little wider. */
const SAFETY = 1.06;

/** How wide `text` sets in the figure's face, in em — what the fit divides the tile's width by. */
export function figureEms(text: string): number {
  let ems = 0;
  for (const ch of text) {
    ems += (ch >= '0' && ch <= '9' ? DIGIT_EMS : (GLYPH_EMS[ch] ?? OTHER_EMS)) + TRACKING_EMS;
  }
  // Never budget less than three digits: a one-character figure ("0") would
  // otherwise be drawn at several times the size of its neighbours.
  return Number((Math.max(ems, 3 * DIGIT_EMS) * SAFETY).toFixed(3));
}
